import React from 'react';
import { Row } from 'reactstrap';

import CancelPopUpContainer from '../../containers/AccountSettingsContainer/cancelPopUpContainer';
import history from '../../utils/history';
import {
  getIosPlanType,
  getIosToStripeSwitchType,
} from '../../utils/subscriptionConstants';
import LoadingGIF from '../Common/Icons/Spinner-0.8s-200px.svg';
import SubscriptionWarningModal from '../Study/StudyMain/SubscriptionWarningModal';
import BillingInformation from './BillingInformation/BillingInformationView';
import PlanItem from './PlanItem/PlanItemView';
import UpdateSubscriptionPlan from './UpdateSubscriptionPlan/UpdateSubscriptionPlanView.js';

const ACTIONS_MAPPER = {
  DEFAULT: 'Your Current Subscription',
  'REVERT-CANCELLATION': 'Your Current Subscription',
  RENEW: 'Renew Your Subscription',
  CHANGE: 'Change Your Subscription',
  UPGRADE: 'Upgrade Your Subscription',
  WEB: 'Subscription on the web',
};

// This is displayed in Account Settings > "Manage my Subscription" or "continue my subscription on the web"
class ManageSubscriptionPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      cancelPopUpOpen: false,
      isEditingSubscription: false,
      isSubscriptionWarningModalOpen: false,
      hasSeenSubscriptionWarningModal: false,
      selectedPlan: null,
    };
  }

  componentWillUnmount() {
    this.props.resetComponent();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.revertCancellationSuccess) {
      this.props.setRevertCancellationSuccess(false);
      history.push('/app/account-settings');
    }

    if (nextProps.subscriptionOperationSuccess) {
      this.props.setSubscriptionOperationSuccess(false);
      history.push('/app/account-settings');
    }

    if (nextProps.refundAmountMessage.text !== '') {
      setTimeout(() => {
        this.props.setRefundAmountMessage({ type: 'success', text: '' });
      }, 15000);
    }
  }

  componentDidMount() {
    this.props.loadPlans();
    const rateId = this.props.user?.rate?.id;
    const rank = this.props.user?.rank;
    const isPfeOnly = this.props.user?.rate?.isPfeOnly;

    if (rateId && rank && !isPfeOnly) {
      this.props.loadQuestionsSKTCounter(rateId, rank, 'Regular');
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const prevRateId = prevProps.user?.rate?.id;
    const currentRateId = this.props.user?.rate?.id;
    const prevRank = prevProps.user?.rank;
    const currentRank = this.props.user?.rank;

    if (
      currentRateId &&
      currentRank &&
      !this.props.user?.rate?.isPfeOnly &&
      (prevRateId !== currentRateId || prevRank !== currentRank)
    ) {
      this.props.loadQuestionsSKTCounter(currentRateId, currentRank, 'Regular');
    }

    if (
      Object.keys(this.props.user).length > 0 &&
      !this.props.user.stripeId &&
      !this.props.user.transactionIosId &&
      !this.state.isSubscriptionWarningModalOpen &&
      !this.state.hasSeenSubscriptionWarningModal
    ) {
      this.setState({
        isSubscriptionWarningModalOpen: true,
        hasSeenSubscriptionWarningModal: true,
      });
    }

    if (prevProps.user?.id !== this.props.user?.id) {
      if (!this.props.user?.active) {
        if (this.props.user?.rate) {
          this.props.getByReference(this.props.user.rate);
        }
      }
    }
  }

  openCancelModal = (value, redirect = false) => {
    this.setState({ cancelPopUpOpen: value });
    if (redirect) {
      setTimeout(() => {
        history.push('/app/account-settings');
      }, 500);
    }
  };

  showPlans = (userSubscription) => {
    const isPfeOnly =
      this.props.user?.rate?.isPfeOnly || this.props.questionsSKTCount === 0;
    if (
      userSubscription?.type === 'iOS' &&
      userSubscription?.status === 'active'
    ) {
      // active apple subscriber
      if (isPfeOnly) {
        // AFSC is PFE only, show only PFE plans
        return this.props.plans?.filter((p) => !p.isOccupational);
      }

      const currentIosSub = getIosPlanType(userSubscription.productId);
      if (
        !currentIosSub?.isOccupational &&
        currentIosSub?.duration === 'Annually'
      ) {
        // user has iOS PFE-only Annual Sub
        // filter out PFE SKT semiannual, switching from PFE Only Annual to this one is not allowed
        // (only applies on iOs to Stripe switch)
        return this.props.plans?.filter(
          (plan) =>
            !(
              plan.isOccupational === true &&
              plan.interval_count === 6 &&
              plan.interval === 'month'
            ),
        );
      }
    }
    // not active apple sub, default case
    return isPfeOnly
      ? this.props.plans?.filter((p) => !p.isOccupational)
      : this.props.plans;
  };

  onPlanSelect = (plan) => this.setState({ selectedPlan: plan });

  getIosToStripeMessage = () => {
    const { userSubscription } = this.props;
    const { selectedPlan } = this.state;

    if (
      !selectedPlan ||
      userSubscription?.type !== 'iOS' ||
      userSubscription?.status !== 'active'
    ) {
      return 'Please complete your billing information to purchase a subscription on the website';
    }

    const switchType = getIosToStripeSwitchType(
      userSubscription.productId,
      selectedPlan,
    );

    if (switchType === 'immediate') {
      return (
        'Your new subscription will activate immediately. ' +
        'A prorated discount will be applied to your first web subscription charge based on your unused Apple subscription time. ' +
        'Remember to cancel your iOS subscription manually from your Apple device settings.'
      );
    }

    if (switchType === 'scheduled') {
      const endDate = new Date(userSubscription.endedDate).toLocaleDateString();
      return (
        `Your new subscription will activate on ${endDate}, ` +
        'when your current Apple subscription expires.'
      );
    }

    return null;
  };

  render() {
    const user = this.props.user;
    const userSubscription = this.props.userSubscription;
    const plans = this.props.plans;

    const cancelSubscriptionCondition =
      userSubscription &&
      (userSubscription.id || userSubscription.id === 0) &&
      user.active &&
      (!userSubscription.isCancelled || userSubscription.scheduledPlan) &&
      !user.isFreeAccount;

    let action = null;
    if (!(this.props.loadingUserSubscription || !user.id)) {
      // DETERMINE ACTION TO TAKE
      if (user.isFreeAccount) {
        action = 'UPGRADE';
      } else {
        if (
          user.transactionIosId ||
          (!user.transactionIosId && !user.stripeId)
        ) {
          action = 'WEB';
        } else {
          if (!user.active && !user.transactionIosId) {
            action = 'RENEW';
          } else {
            if (
              userSubscription?.isCancelled &&
              !userSubscription.scheduledPlan
            ) {
              if (userSubscription.type === 'stripe') {
                action = 'REVERT-CANCELLATION';
              } else {
                action = 'RENEW';
              }
            } else {
              if (userSubscription.cancel_at) {
                action = 'RENEW';
              } else {
                action = 'DEFAULT';
              }
            }
          }
        }
      }
    }

    return this.props.loadingUserSubscription ||
      !user.id ||
      plans.length === 0 ||
      !action ||
      this.props.isLoadingSktData ? (
      <>
        <Row className="manage-subscription-loading mx-0 py-4 px-2">
          <img
            className="loading-subscription-gif"
            alt="loading"
            src={LoadingGIF}
            height="200"
            width="200"
          />
        </Row>
        <SubscriptionWarningModal
          isOpen={this.state.isSubscriptionWarningModalOpen}
          toggle={() =>
            this.setState({
              isSubscriptionWarningModalOpen:
                !this.state.isSubscriptionWarningModalOpen,
            })
          }
        />
      </>
    ) : (
      <>
        <Row className="manage-subscription-page mx-0 py-4 px-2">
          <div className="left-column" sm={12} md={12} lg={6}>
            {action && (
              <div className="primary-title">{ACTIONS_MAPPER[action]}</div>
            )}
            {this.state.isEditingSubscription ? (
              <UpdateSubscriptionPlan
                plans={this.showPlans(userSubscription)}
                userSubscription={userSubscription}
                openCancelModal={() => this.openCancelModal(true)}
                cancelSubscriptionCondition={cancelSubscriptionCondition}
                updateSubscriptionPlan={this.props.updateSubscriptionPlan}
                waiting={this.props.updateSubscriptionPlanWaiting}
                message={this.props.updateSubscriptionPlanMessage}
                success={this.props.updateSubscriptionPlanSuccess}
                user={this.props.user}
                closeUpdateSubscriptionPlan={() =>
                  this.setState({
                    isEditingSubscription: false,
                  })
                }
                refundAmountMessage={this.props.refundAmountMessage}
              />
            ) : action !== 'RENEW' && action !== 'WEB' ? (
              <>
                <PlanItem
                  action={action}
                  userSubscription={userSubscription}
                  plans={this.showPlans(userSubscription)}
                  user={user}
                />
                {userSubscription.card ? (
                  <div
                    className="cancel-link"
                    data-test="edit-subscription-button"
                    onClick={() =>
                      this.setState({ isEditingSubscription: true })
                    }
                  >
                    Edit Subscription
                  </div>
                ) : (
                  <div className="mt-3">
                    Please update your payment details in order to update your
                    subscription
                  </div>
                )}
              </>
            ) : (
              action === 'WEB' && (
                <div className="mt-3 mb-3 px-3 sticky-top">
                  {this.getIosToStripeMessage()}
                </div>
              )
            )}
            {this.props.updateSubscriptionPlanMessage &&
              !this.props.updateSubscriptionPlanWaiting && (
                <>
                  {this.props.updateSubscriptionPlanMessage.type ===
                  'success' ? (
                    <div className="message-success text-center ml-0">
                      {this.props.updateSubscriptionPlanMessage.text}
                    </div>
                  ) : (
                    <div className="message error">
                      {this.props.updateSubscriptionPlanMessage.text}
                    </div>
                  )}
                </>
              )}
          </div>
          <div className="right-column" sm={12} md={12} lg={6}>
            <div className="primary-title">Billing Information</div>
            <BillingInformation
              contentByRef={this.props.contentByRef}
              plans={this.showPlans(userSubscription)}
              onPlanSelect={this.onPlanSelect}
              activateSubscription={this.props.activateSubscription}
              subscriptionActivationMessage={
                this.props.subscriptionActivationMessage
              }
              subscriptionActivationWaiting={
                this.props.subscriptionActivationWaiting
              }
              subscriptionActivationSucces={
                this.props.subscriptionActivationSucces
              }
              action={action}
              userSubscription={userSubscription}
              user={this.props.user}
              revertCancellation={() =>
                this.props.revertCancellation(userSubscription.id)
              }
              switchSubscriptionToStripe={(switchSubscription) =>
                this.props.switchSubscriptionToStripe(switchSubscription)
              }
              renewSubscription={(newSubscription) =>
                this.props.renewSubscription(newSubscription)
              }
              changeSubscription={this.props.changeSubscription}
              upgradeSubscription={this.props.upgradeSubscription}
              editPaymentDetails={this.props.editPaymentDetails}
              revertCancellationMessage={this.props.revertCancellationMessage}
              setRevertCancellationMessage={
                this.props.setRevertCancellationMessage
              }
              revertCancellationWaiting={this.props.revertCancellationWaiting}
              subscriptionOperationWaiting={
                this.props.subscriptionOperationWaiting
              }
              subscriptionOperationMessage={
                this.props.subscriptionOperationMessage
              }
              setSubscriptionOperationMessage={
                this.props.setSubscriptionOperationMessage
              }
              setSubscriptionOperationSuccess={
                this.props.setSubscriptionOperationSuccess
              }
              subscriptionOperationSuccess={
                this.props.subscriptionOperationSuccess
              }
              editPaymentDetailsMessage={this.props.editPaymentDetailsMessage}
              editPaymentDetailsSuccess={this.props.editPaymentDetailsSuccess}
              editPaymentDetailsWaiting={this.props.editPaymentDetailsWaiting}
              setEditPaymentDetailsMessage={
                this.props.setEditPaymentDetailsMessage
              }
              setEditPaymentDetailsSuccess={
                this.props.setEditPaymentDetailsSuccess
              }
              checkCoupon={this.props.checkCoupon}
              couponCheck={this.props.couponCheck}
            />
          </div>
          {userSubscription && (
            <CancelPopUpContainer
              isOpen={this.state.cancelPopUpOpen}
              onClose={(value, redirect = false) =>
                this.openCancelModal(value, redirect)
              }
              nextBillDate={userSubscription.next_bill_date}
              cancelSubscriptionMessage={this.props.cancelSubscriptionMessage}
              cancelSubscriptionWaiting={this.props.cancelSubscriptionWaiting}
              cancelSubscriptionSuccess={this.props.cancelSubscriptionSuccess}
              setCancelSubscriptionMessage={
                this.props.setCancelSubscriptionMessage
              }
              setCancelSubscriptionSuccess={
                this.props.setCancelSubscriptionSuccess
              }
              cancelSubscription={(message) =>
                this.props.cancelSubscription(userSubscription.id, message)
              }
              revertCancellation={() =>
                this.props.revertCancellation(userSubscription.id)
              }
              userId={this.props.user.id}
            />
          )}
        </Row>
      </>
    );
  }
}

export default ManageSubscriptionPage;
